import type { Control, FieldValues, Path } from 'react-hook-form';
import { Info } from 'lucide-react';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const ACTIVE_LABEL = 'Active';

interface FormStatusFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: Path<TValues>;
  label?: string | undefined;
  description?: string | undefined;
  hint?: string | undefined;
}

interface StatusHintProps {
  label: string;
  hint: string;
}

function StatusHint({ label, hint }: StatusHintProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span tabIndex={0} aria-label={`${label}: ${hint}`} />}
        className="inline-flex cursor-help text-muted-foreground transition-colors hover:text-primary focus-visible:text-primary"
      >
        <Info className="size-3.5" aria-hidden />
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

export function FormStatusField<TValues extends FieldValues>({
  control,
  name,
  label = ACTIVE_LABEL,
  description,
  hint,
}: FormStatusFieldProps<TValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex-row items-center justify-between gap-4 rounded-xl bg-primary/3 px-3.5 py-3 ring-1 ring-primary/8">
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1.5 text-sm leading-none font-medium">
              {label}
              {hint === undefined ? null : <StatusHint label={label} hint={hint} />}
            </span>
            {description === undefined ? null : <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </div>
          <FormControl>
            <Switch
              aria-label={label}
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked)}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}
